import { useCallback, useState } from 'react';
import {
  Form,
  FormSizes,
  Field,
  TextInput,
  Checkbox,
  Button,
  ButtonSizes,
  Alert,
  AlertVariants,
  AlertAppearances,
  AlertOrientations,
  SocialLoginButton,
  SocialLoginProviders,
  Typography,
  TypographyVariants,
  TypographyColors,
  TypographyElements,
  FormValidateOn,
} from '@carboledger/ui';
import { PasswordRequirementChecklist } from './PasswordRequirementChecklist';
import { NewPasswordField, ConfirmPasswordField } from './PasswordFields';
import styles from './SignupForm.module.scss';
import { BRAND_NAME } from '@/components/layout/layout.constants';
import { useTranslations } from '@/hooks';

/**
 * Props:
 *   onSubmit         ({ firstName, lastName, password, confirmPassword }) => void
 *   isLoading        true while the completeRegistration call is in flight
 *   error            RTK Query error from that call, or undefined
 *   onSocialSignup   (provider: 'google' | 'microsoft') => void — SSO click
 */
export const SignupForm = ({ onSubmit, isLoading, error, onSocialSignup }) => {
  const t = useTranslations();
  const [showPassword, setShowPassword] = useState(false);

  const handleShowPassword = useCallback((event) => {
    setShowPassword(event.target.checked);
  }, []);
  const handleMicrosoftSignup = useCallback(() => {
    onSocialSignup?.(SocialLoginProviders.microsoft);
  }, [onSocialSignup]);
  const handleGoogleSignup = useCallback(() => {
    onSocialSignup?.(SocialLoginProviders.google);
  }, [onSocialSignup]);

  return (
    <Form
      validateOn={FormValidateOn.change}
      initialValues={{ firstName: '', lastName: '', password: '', confirmPassword: '' }}
      onSubmit={onSubmit}
      size={FormSizes.small}
      className={styles.root}
    >
      {(form) => (
        <>
          <Typography
            variant={TypographyVariants.h6}
            as={TypographyElements.h1}
            className={styles.heading}
          >
            {t('AUTH_SIGNUP_WELCOME', { brand: BRAND_NAME })}
          </Typography>

          {error && (
            <Alert
              variant={AlertVariants.danger}
              appearance={AlertAppearances.accent}
              orientation={AlertOrientations.horizontal}
              className={styles.alert}
            >
              {error.data?.message ?? t('ERROR_GENERIC')}
            </Alert>
          )}

          <div className={styles.fields}>
            <Field name="firstName" required>
              {({ id, name, value, onChange, onBlur, status, message, required }) => (
                <TextInput
                  id={id}
                  name={name}
                  label={t('AUTH_FIRST_NAME_LABEL')}
                  placeholder={t('AUTH_FIRST_NAME_PLACEHOLDER')}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  status={status}
                  message={message}
                  required={required}
                />
              )}
            </Field>

            <Field name="lastName" required>
              {({ id, name, value, onChange, onBlur, status, message, required }) => (
                <TextInput
                  id={id}
                  name={name}
                  label={t('AUTH_LAST_NAME_LABEL')}
                  placeholder={t('AUTH_LAST_NAME_PLACEHOLDER')}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  status={status}
                  message={message}
                  required={required}
                />
              )}
            </Field>

            <div className={styles.passwordField}>
              <NewPasswordField showPassword={showPassword} />
              <Checkbox
                label={t('AUTH_SHOW_PASSWORD')}
                checked={showPassword}
                onChange={handleShowPassword}
              />
            </div>

            <div className={styles.passwordField}>
              <ConfirmPasswordField showPassword={showPassword} />
              <PasswordRequirementChecklist password={form.values.password} />
            </div>
          </div>

          <Button type="submit" fullWidth size={ButtonSizes.sm} loading={isLoading}>
            {t('AUTH_CREATE_ACCOUNT_ACTION')}
          </Button>

          <div className={styles.divider}>
            <span className={styles.dividerLine} />
            <Typography
              variant={TypographyVariants.body}
              color={TypographyColors.secondary}
              as={TypographyElements.span}
            >
              {t('AUTH_OR')}
            </Typography>
            <span className={styles.dividerLine} />
          </div>

          <div className={styles.sso}>
            <SocialLoginButton
              provider={SocialLoginProviders.microsoft}
              onClick={handleMicrosoftSignup}
            >
              {t('AUTH_LOGIN_MICROSOFT')}
            </SocialLoginButton>
            <SocialLoginButton provider={SocialLoginProviders.google} onClick={handleGoogleSignup}>
              {t('AUTH_LOGIN_GOOGLE')}
            </SocialLoginButton>
          </div>
        </>
      )}
    </Form>
  );
};
